import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Lock, RefreshCw, WifiOff } from "lucide-react";
import { Mini, type Area } from "./components/Tactile";
import { ReceiptCameraLink } from "./components/ReceiptCameraLink";
import { AuthProvider, useAuth } from "./lib/auth";
import { I18nProvider, useI18n } from "./lib/i18n";
import { Link, RouterProvider, useRouter } from "./lib/router";
import { Loading, Spinner, ToastProvider } from "./components/ui";
import AuthPage from "./pages/AuthPage";
import LandingPage from "./pages/landing/LandingPage";
import HomePage from "./pages/HomePage";
import { Paywall, type PaidFeature } from "./components/Paywall";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { warmUpOcr } from "./lib/ocr";
import { reloadToLatest, useUpdateAvailable } from "./lib/update";
import { PullToRefresh } from "./components/PullToRefresh";
import { ExportViewer } from "./components/ExportViewer";
import { FileReady } from "./components/FileReady";

const ShoppingPage = lazy(() => import("./pages/shopping/ShoppingPage"));
const SharedListPage = lazy(() => import("./pages/shopping/SharedListPage"));
const LegalPage = lazy(() => import("./pages/legal/LegalPage"));
const SupportPage = lazy(() => import("./pages/account/SupportPage"));
const WalletPage = lazy(() => import("./pages/wallet/WalletPage"));
const LedgerPage = lazy(() => import("./pages/ledger/LedgerPage"));
const NutritionPage = lazy(() => import("./pages/nutrition/NutritionPage"));
const GoodsPage = lazy(() => import("./pages/goods/GoodsPage"));
const ReportPage = lazy(() => import("./pages/report/ReportPage"));
const BoardPage = lazy(() => import("./pages/community/BoardPage"));
const PlacesPage = lazy(() => import("./pages/places/PlacesPage"));
const AccountPage = lazy(() => import("./pages/account/AccountPage"));
const PlansPage = lazy(() => import("./pages/account/PlansPage"));
const AdminPage = lazy(() => import("./pages/account/AdminPage"));
const FridgePage = lazy(() => import("./pages/fridge/FridgePage"));

export function App() {
  return (
    <I18nProvider>
      <RouterProvider>
        <ToastProvider>
          <AuthProvider>
            <Shell />
          </AuthProvider>
        </ToastProvider>
      </RouterProvider>
    </I18nProvider>
  );
}

/** Pages that need Plus (trial or subscription). The shopping list, card wallet, nearby shops, home and "Me" are free. */
function paidFeature(path: string): PaidFeature | null {
  if (path === "/ledger/new") return "receipt";
  if (path === "/ledger" || path.startsWith("/ledger/")) return "ledger";
  if (path === "/meals") return "meals";
  if (path === "/fridge") return "fridge";
  if (path === "/goods") return "goods";
  if (path === "/report") return "ledger";
  return null;
}

/** Route table. Ledger owns everything under /ledger (records, insights, record detail). */
function resolve(path: string, plus: boolean): { node: ReactNode; tab: string | null } {
  const paid = paidFeature(path);
  if (paid && !plus) return { node: <Paywall feature={paid} />, tab: paid === "ledger" || paid === "receipt" || paid === "goods" ? "/ledger" : paid === "meals" ? "/meals" : null };
  if (path === "/" || path === "/login" || path === "/register") return { node: <HomePage />, tab: "/" };
  if (path === "/shopping") return { node: <ShoppingPage />, tab: "/shopping" };
  if (path === "/wallet") return { node: <WalletPage />, tab: null };
  if (path === "/ledger" || path.startsWith("/ledger/")) return { node: <LedgerPage />, tab: "/ledger" };
  if (path === "/meals") return { node: <NutritionPage />, tab: "/meals" };
  if (path === "/board") return { node: <BoardPage />, tab: null };
  if (path === "/places") return { node: <PlacesPage />, tab: null };
  if (path === "/fridge") return { node: <FridgePage />, tab: null };
  if (path === "/goods") return { node: <GoodsPage />, tab: "/ledger" };
  if (path === "/report") return { node: <ReportPage />, tab: "/ledger" };
  if (path === "/me") return { node: <AccountPage />, tab: "/me" };
  if (path === "/me/plans") return { node: <PlansPage />, tab: "/me" };
  if (path === "/me/admin") return { node: <AdminPage />, tab: "/me" };
  if (path === "/me/support") return { node: <SupportPage />, tab: "/me" };
  return { node: <HomePage />, tab: "/" };
}

function Shell() {
  const { me, loading, unreachable, refresh } = useAuth();
  const updateReady = useUpdateAvailable();
  const { route } = useRouter();
  const { t } = useI18n();
  const signedIn = !!me;
  // Get the photo reader (receipts, labels, cards) ready while the app sits idle, so the first photo is read
  // straight away instead of first downloading the engine. Skipped when the phone asks to save data.
  useEffect(() => {
    if (!signedIn) return;
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (conn?.saveData || /2g/.test(conn?.effectiveType ?? "")) return;
    const idle = (cb: () => void) => (typeof window.requestIdleCallback === "function" ? window.requestIdleCallback(cb, { timeout: 8000 }) : setTimeout(cb, 3000));
    const timer = window.setTimeout(() => idle(warmUpOcr), 2500);
    return () => window.clearTimeout(timer);
  }, [signedIn]);
  if (!me && unreachable) {
    // First load failed: we don't know yet whether the person is signed in.
    return (
      <div className="auth">
        <div className="empty">
          <WifiOff size={36} />
          <h3>{t("error.offline")}</h3>
          <Spinner label={t("common.retrying")} />
          <button className="btn secondary" onClick={refresh}>
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="auth">
        <Loading />
      </div>
    );
  }
  // Privacy policy, terms and company details: readable with or without an account.
  const legal = ({ "/privacy": "privacy", "/terms": "terms", "/refund": "refund", "/imprint": "imprint" } as const)[route.path as "/privacy"];
  if (legal)
    return (
      <Suspense fallback={<Loading />}>
        <LegalPage doc={legal} />
      </Suspense>
    );
  // A family's shared shopping list from its link: opens with or without an account.
  const listToken = /^\/list\/([\w-]{10,64})$/.exec(route.path)?.[1];
  if (listToken)
    return (
      <Suspense fallback={<Loading />}>
        <SharedListPage token={listToken} />
      </Suspense>
    );
  if (!me) {
    // Signed-out visitors: landing page, or the sign-in / sign-up forms.
    if (route.path === "/login") return <AuthPage mode="login" />;
    if (route.path === "/register") return <AuthPage mode="register" />;
    if (route.path === "/forgot") return <AuthPage mode="forgot" />;
    if (route.path === "/reset") return <AuthPage mode="reset" />;
    return <LandingPage />;
  }
  // A reset link opened while signed in (e.g. on another device) still works.
  if (route.path === "/reset") return <AuthPage mode="reset" />;
  const { node, tab } = resolve(route.path, me.access.plus);
  return (
    <div className="app">
      {unreachable && (
        <div className="offline-bar" role="status">
          <WifiOff size={16} /> {t("common.offlineBar")}
        </div>
      )}
      <PullToRefresh />
      {/* A newer version is on the server: one tap loads it (never on its own, so nothing typed is lost). */}
      {updateReady && !unreachable && (
        <button type="button" className="offline-bar update-bar" onClick={reloadToLatest}>
          <RefreshCw size={16} /> {t("common.updateBar")} <strong>{t("common.updateNow")}</strong>
        </button>
      )}
      {/* A crash in one page shows a friendly note instead of a blank screen; moving to another section
          resets it. Keyed by section (not the full address), so moving inside one — a receipt form closing
          back to the ledger — keeps the page and what it is showing. */}
      <ErrorBoundary key={route.path.split("/")[1] ?? ""}>
        <Suspense fallback={<Loading />}>{node}</Suspense>
      </ErrorBoundary>
      <TabBar active={tab} plus={!!me?.access.plus} />
      <FileReady />
      <ExportViewer />
    </div>
  );
}

/**
 * Six tabs: home, list, the receipt camera (raised in the middle), money, meals and "Me" (where signing out is).
 * Each tab is a small pressable tile in its part's colour; paid tabs carry a small lock for people without Plus.
 */
function TabBar({ active, plus }: { active: string | null; plus: boolean }) {
  const { t } = useI18n();
  const tabs: { to: string; label: string; area: Area; paid?: boolean; cap?: boolean }[] = [
    { to: "/", label: t("nav.home"), area: "home" },
    { to: "/shopping", label: t("nav.shopping"), area: "shop" },
    { to: "/ledger/new?scan=1", label: t("home.quick.receipt"), area: "receipt", paid: true, cap: true },
    { to: "/ledger", label: t("nav.ledger"), area: "ledger", paid: true },
    { to: "/meals", label: t("nav.meals"), area: "food", paid: true },
    { to: "/me", label: t("nav.me"), area: "me" },
  ];
  return (
    <nav className="tabbar" aria-label="Main">
      <div className="tabbar-inner">
        {tabs.map((x) => {
          const face = (
            <>
              <span className="tab-icon">
                <Mini area={x.area} size={x.cap ? "lg" : "sm"} />
                {x.paid && !plus && (
                  <span className="tab-lock" aria-label={t("home.locked")}>
                    <Lock size={10} strokeWidth={3} />
                  </span>
                )}
              </span>
              <span className="tab-label">{x.label}</span>
            </>
          );
          return x.cap ? (
            <ReceiptCameraLink key={x.to} to={x.to} enabled={plus} className="tab cap">
              {face}
            </ReceiptCameraLink>
          ) : (
            <Link key={x.to} to={x.to} className="tab" aria-current={active === x.to ? "page" : undefined}>
              {face}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
